import { useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useEmployeeViewSettings } from "@/hooks/useEmployeeViewSettings";
import { RestrictedPageNotice } from "@/components/RestrictedPageNotice";
import { useBranches } from "@/hooks/useBranches";
import { DeliveryNote, type DeliveryNoteData } from "@/components/transport/DeliveryNote";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  Truck,
  Package,
  AlertTriangle,
  Pencil,
  Trash2,
  MapPin,
  ChevronRight,
  Printer,
  Loader2,
} from "lucide-react";
import { NoMatchingRecords } from "@/components/NoMatchingRecords";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { format, addDays, isBefore, isAfter } from "date-fns";
import { useInvalidateReorderQueries } from "@/hooks/useReorderIntelligence";

type POStatus = "draft" | "sent" | "confirmed" | "partial" | "received" | "cancelled";
type DeliveryStatus = "planned" | "in_transit" | "delivered";

interface Supplier {
  id: string;
  name: string;
  contact?: string | null;
  contact_person?: string | null;
  phone?: string | null;
  email?: string | null;
  category?: string | null;
}

interface PurchaseOrder {
  id: string;
  business_id: string;
  supplier_id: string;
  po_number: string | null;
  status: POStatus;
  order_date: string;
  expected_delivery_date: string | null;
  notes: string | null;
  supplier?: { name: string } | null;
}

interface PurchaseOrderLine {
  id: string;
  purchase_order_id: string;
  inventory_id: string | null;
  description: string | null;
  quantity: number;
  unit_cost: number;
  inventory?: { name: string; sku: string } | null;
}

interface Delivery {
  id: string;
  purchase_order_id: string;
  branch_id: string | null;
  delivery_date: string;
  status: DeliveryStatus;
  carrier_tracking: string | null;
  notes: string | null;
  received_at: string | null;
  received_by?: string | null;
  vehicle_reg?: string | null;
  doc_number?: string | null;
  delivery_notes?: string | null;
  branch?: { name: string } | null;
}

interface InventoryRow {
  id: string;
  name: string;
  sku: string;
  current_stock?: number;
}

const PO_STATUS_OPTIONS: { value: POStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "confirmed", label: "Confirmed" },
  { value: "partial", label: "Partial" },
  { value: "received", label: "Received" },
  { value: "cancelled", label: "Cancelled" },
];

const DELIVERY_STATUS_OPTIONS: { value: DeliveryStatus; label: string }[] = [
  { value: "planned", label: "Planned" },
  { value: "in_transit", label: "In Transit" },
  { value: "delivered", label: "Delivered" },
];

export default function TransportLogisticsPage() {
  const { user, businessId, profile, role } = useAuth();
  const { settings: employeeViewSettings } = useEmployeeViewSettings();
  const { branches } = useBranches();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [inventory, setInventory] = useState<InventoryRow[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [poLines, setPoLines] = useState<PurchaseOrderLine[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [activeTab, setActiveTab] = useState("orders");
  const [addPoOpen, setAddPoOpen] = useState(false);
  const [editPo, setEditPo] = useState<PurchaseOrder | null>(null);
  const [addDeliveryOpen, setAddDeliveryOpen] = useState(false);
  const [addDeliveryPoId, setAddDeliveryPoId] = useState<string | null>(null);
  const [receiveDelivery, setReceiveDelivery] = useState<Delivery | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterSupplier, setFilterSupplier] = useState<string>("all");
  const [successBanner, setSuccessBanner] = useState<string | null>(null);
  const { toast } = useToast();
  const invalidateReorderQueries = useInvalidateReorderQueries();

  // PO form state
  const [poSupplierId, setPoSupplierId] = useState("");
  const [poOrderDate, setPoOrderDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [poExpectedDate, setPoExpectedDate] = useState("");
  const [poNotes, setPoNotes] = useState("");
  const [poLinesForm, setPoLinesForm] = useState<{ inventory_id: string | null; description: string; quantity: number; unit_cost: number }[]>([]);
  const [addLineType, setAddLineType] = useState<"inventory" | "freetext">("inventory");

  // Delivery form state
  const [deliveryDate, setDeliveryDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [deliveryStatus, setDeliveryStatus] = useState<DeliveryStatus>("planned");
  const [deliveryBranchId, setDeliveryBranchId] = useState<string>("");
  const [deliveryTracking, setDeliveryTracking] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState("");

  // Receive form state
  const [receiveQuantities, setReceiveQuantities] = useState<Record<string, number>>({});
  const [receiveReceivedBy, setReceiveReceivedBy] = useState("");
  const [receiveVehicleReg, setReceiveVehicleReg] = useState("");
  const [receiveDeliveryNotes, setReceiveDeliveryNotes] = useState("");
  const [receiving, setReceiving] = useState(false);

  // Print delivery note
  const [printDeliveryId, setPrintDeliveryId] = useState<string | null>(null);

  const effectiveBizId = businessId ?? null;

  const fetchSuppliers = async () => {
    if (!effectiveBizId) return;
    const q = supabase.from("suppliers").select("id, name, contact, contact_person, phone, email, category").order("name").eq("business_id", effectiveBizId);
    const { data } = await q;
    setSuppliers((data ?? []) as Supplier[]);
  };

  const fetchInventory = async () => {
    if (!effectiveBizId) return;
    const q = supabase.from("inventory").select("id, name, sku").order("name").eq("business_id", effectiveBizId);
    const { data } = await q;
    setInventory((data ?? []) as InventoryRow[]);
  };

  const fetchPurchaseOrders = async () => {
    if (!effectiveBizId) return;
    const q = supabase
      .from("purchase_orders")
      .select("id, business_id, supplier_id, po_number, status, order_date, expected_delivery_date, notes, suppliers(name)")
      .order("order_date", { ascending: false })
      .eq("business_id", effectiveBizId);
    const { data } = await q;
    const rows = (data ?? []).map((r: Record<string, unknown>) => {
      const s = r.suppliers ?? r.supplier;
      return { ...r, supplier: (s && typeof s === "object" && "name" in s ? (s as { name: string }) : null) };
    });
    setPurchaseOrders(rows as PurchaseOrder[]);
  };

  const fetchPoLines = async (poIds: string[]) => {
    if (poIds.length === 0) return;
    const { data } = await supabase
      .from("purchase_order_lines")
      .select("id, purchase_order_id, inventory_id, description, quantity, unit_cost, inventory(name, sku)")
      .in("purchase_order_id", poIds);
    const rows = (data ?? []).map((r: Record<string, unknown>) => ({
      ...r,
      inventory: (r.inventory as { name: string; sku: string } | null) ?? null,
    }));
    setPoLines(rows as PurchaseOrderLine[]);
  };

  const fetchDeliveries = async () => {
    const { data } = await supabase
      .from("deliveries")
      .select("id, purchase_order_id, branch_id, delivery_date, status, carrier_tracking, notes, received_at, received_by, vehicle_reg, doc_number, delivery_notes")
      .order("delivery_date", { ascending: false });
    const rows = (data ?? []).map((r: Record<string, unknown>) => ({
      ...r,
      branch: null,
    }));
    setDeliveries(rows as Delivery[]);
  };

  useEffect(() => {
    fetchSuppliers();
    fetchInventory();
  }, [effectiveBizId]);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [effectiveBizId]);

  useEffect(() => {
    const ids = [...new Set(purchaseOrders.map((po) => po.id))];
    if (ids.length > 0) fetchPoLines(ids);
  }, [purchaseOrders.length > 0 ? purchaseOrders.map((p) => p.id).join(",") : ""]);

  useEffect(() => {
    fetchDeliveries();
  }, [effectiveBizId, purchaseOrders.length]);

  const showBanner = (msg: string) => {
    setSuccessBanner(msg);
    setTimeout(() => setSuccessBanner(null), 4000);
  };

  const today = format(new Date(), "yyyy-MM-dd");
  const nextWeek = format(addDays(new Date(), 7), "yyyy-MM-dd");

  const overduePos = useMemo(
    () =>
      purchaseOrders.filter(
        (po) =>
          po.expected_delivery_date &&
          isBefore(po.expected_delivery_date, today) &&
          !["received", "cancelled"].includes(po.status)
      ),
    [purchaseOrders, today]
  );
  const dueSoonPos = useMemo(
    () =>
      purchaseOrders.filter(
        (po) =>
          po.expected_delivery_date &&
          !isBefore(po.expected_delivery_date, today) &&
          !isAfter(po.expected_delivery_date, nextWeek) &&
          !["received", "cancelled"].includes(po.status)
      ),
    [purchaseOrders, today, nextWeek]
  );

  const filteredPos = useMemo(() => {
    let list = purchaseOrders;
    if (filterStatus !== "all") list = list.filter((po) => po.status === filterStatus);
    if (filterSupplier !== "all") list = list.filter((po) => po.supplier_id === filterSupplier);
    return list;
  }, [purchaseOrders, filterStatus, filterSupplier]);

  const getLineDisplay = (line: PurchaseOrderLine) =>
    line.inventory ? `${line.inventory.name} (${line.inventory.sku})` : line.description || "—";

  const addPoLine = () => {
    if (addLineType === "inventory" && inventory.length > 0) {
      setPoLinesForm((prev) => [...prev, { inventory_id: inventory[0].id, description: inventory[0].name ?? "", quantity: 1, unit_cost: 0 }]);
    } else {
      setPoLinesForm((prev) => [...prev, { inventory_id: null, description: "", quantity: 1, unit_cost: 0 }]);
    }
  };

  const removePoLine = (idx: number) => {
    setPoLinesForm((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleCreatePo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveBizId) {
      toast({ title: "No business", description: "Link your account to a business.", variant: "destructive" });
      return;
    }
    const bizId = effectiveBizId ?? (purchaseOrders[0]?.business_id ?? "");
    if (!bizId) {
      toast({ title: "Error", description: "No business ID.", variant: "destructive" });
      return;
    }
    if (!poSupplierId || poLinesForm.length === 0) {
      toast({ title: "Required", description: "Select supplier and add at least one line.", variant: "destructive" });
      return;
    }

    const { data: po, error: poErr } = await supabase
      .from("purchase_orders")
      .insert({
        business_id: bizId,
        supplier_id: poSupplierId,
        order_date: poOrderDate,
        expected_delivery_date: poExpectedDate || null,
        notes: poNotes || null,
        created_by: user?.id ?? null,
      })
      .select("id")
      .single();

    if (poErr || !po) {
      toast({ title: "Error", description: poErr?.message ?? "Failed to create PO.", variant: "destructive" });
      return;
    }

    const lines = poLinesForm.map((l) => ({
      purchase_order_id: po.id,
      inventory_id: l.inventory_id || null,
      description: l.description || null,
      quantity: l.quantity,
      unit_cost: l.unit_cost,
    }));
    const { error: linesErr } = await supabase.from("purchase_order_lines").insert(lines);
    if (linesErr) {
      toast({ title: "Error", description: linesErr.message, variant: "destructive" });
      await supabase.from("purchase_orders").delete().eq("id", po.id);
      return;
    }

    toast({ title: "Purchase order created" });
    showBanner("Purchase order created");
    setAddPoOpen(false);
    resetPoForm();
    fetchPurchaseOrders();
  };

  const resetPoForm = () => {
    setPoSupplierId("");
    setPoOrderDate(format(new Date(), "yyyy-MM-dd"));
    setPoExpectedDate("");
    setPoNotes("");
    setPoLinesForm([]);
    setEditPo(null);
  };

  const handleUpdatePoStatus = async (id: string, status: POStatus) => {
    const { error } = await supabase.from("purchase_orders").update({ status }).eq("id", id);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else {
      toast({ title: "PO updated" });
      fetchPurchaseOrders();
    }
  };

  const handleDeletePo = async (id: string) => {
    const { error } = await supabase.from("purchase_orders").delete().eq("id", id);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else {
      toast({ title: "PO deleted" });
      fetchPurchaseOrders();
      fetchDeliveries();
    }
  };

  const openAddDelivery = (poId: string) => {
    setAddDeliveryPoId(poId);
    setDeliveryDate(format(new Date(), "yyyy-MM-dd"));
    setDeliveryStatus("planned");
    setDeliveryBranchId("");
    setDeliveryTracking("");
    setDeliveryNotes("");
    setAddDeliveryOpen(true);
  };

  const handleCreateDelivery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addDeliveryPoId) {
      toast({ title: "Error", description: "No purchase order selected. Please try again.", variant: "destructive" });
      return;
    }
    const { error } = await supabase.from("deliveries").insert({
      purchase_order_id: addDeliveryPoId,
      branch_id: deliveryBranchId || null,
      delivery_date: deliveryDate,
      status: deliveryStatus,
      carrier_tracking: deliveryTracking || null,
      notes: deliveryNotes || null,
    });
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Delivery added" });
    showBanner("Delivery added");
    setAddDeliveryOpen(false);
    setAddDeliveryPoId(null);
    fetchDeliveries();
  };

  const handleUpdateDeliveryStatus = async (id: string, status: DeliveryStatus) => {
    const { error } = await supabase.from("deliveries").update({ status }).eq("id", id);
    if (error) toast({ title: "Error", description: error.message, variant: "destructive" });
    else {
      toast({ title: "Delivery updated" });
      fetchDeliveries();
    }
  };

  const openReceive = (d: Delivery) => {
    setReceiveDelivery(d);
    setReceiveReceivedBy("");
    setReceiveVehicleReg("");
    setReceiveDeliveryNotes("");
    const lines = poLines.filter((l) => l.purchase_order_id === d.purchase_order_id);
    const qty: Record<string, number> = {};
    lines.forEach((l) => {
      qty[l.id] = l.quantity;
    });
    setReceiveQuantities(qty);
  };

  const handleReceive = async () => {
    if (!receiveDelivery) return;
    const lines = Object.entries(receiveQuantities)
      .filter(([, q]) => q > 0)
      .map(([id, q]) => ({ purchase_order_line_id: id, quantity_received: q }));
    if (lines.length === 0) {
      toast({ title: "Enter quantities", description: "Add at least one quantity to receive.", variant: "destructive" });
      return;
    }
    setReceiving(true);

    const { error: updateErr } = await supabase
      .from("deliveries")
      .update({
        received_by: receiveReceivedBy.trim() || null,
        vehicle_reg: receiveVehicleReg.trim() || null,
        delivery_notes: receiveDeliveryNotes.trim() || null,
      })
      .eq("id", receiveDelivery.id);

    if (updateErr) {
      setReceiving(false);
      toast({ title: "Error", description: updateErr.message, variant: "destructive" });
      return;
    }

    const { error } = await supabase.rpc("receive_delivery", {
      p_delivery_id: receiveDelivery.id,
      p_lines: lines,
    });
    setReceiving(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Stock received" });
    showBanner("Stock received successfully");
    setReceiveDelivery(null);
    invalidateReorderQueries();
    fetchPurchaseOrders();
    fetchDeliveries();
    fetchInventory();
  };

  const supplierName = (id: string) => suppliers.find((s) => s.id === id)?.name ?? "—";
  const branchName = (id: string | null) => branches.find((b) => b.id === id)?.name ?? (id ? "—" : "—");

  const fetchDeliveryNoteData = useCallback(async (deliveryId: string): Promise<DeliveryNoteData | null> => {
    const { data: delivery, error: delErr } = await supabase
      .from("deliveries")
      .select("id, purchase_order_id, branch_id, delivery_date, received_by, vehicle_reg, doc_number, delivery_notes, updated_at")
      .eq("id", deliveryId)
      .single();
    if (delErr || !delivery) return null;

    const poId = delivery.purchase_order_id;
    const poForBiz = await supabase.from("purchase_orders").select("business_id").eq("id", poId).single();
    const bizId = (poForBiz.data as { business_id?: string } | null)?.business_id ?? effectiveBizId;
    if (!bizId) return null;

    const [poRes, dlRes, bizRes, profRes, branchRes] = await Promise.all([
      supabase.from("purchase_orders").select("id, po_number, supplier_id, business_id").eq("id", poId).single(),
      supabase.from("delivery_lines").select("id, purchase_order_line_id, quantity_received").eq("delivery_id", deliveryId),
      supabase.from("businesses").select("name").eq("id", bizId).maybeSingle(),
      supabase.from("business_profiles").select("phone_number, business_email").eq("business_id", bizId).maybeSingle(),
      delivery.branch_id
        ? supabase.from("branches").select("name").eq("id", delivery.branch_id).single()
        : Promise.resolve({ data: null }),
    ]);

    const po = poRes.data as { id: string; po_number: string | null; supplier_id: string; business_id: string } | null;
    if (!po) return null;

    const [supplierRes, polRes] = await Promise.all([
      supabase.from("suppliers").select("name, phone, email").eq("id", po.supplier_id).single(),
      supabase
        .from("purchase_order_lines")
        .select("id, inventory_id, description, quantity, inventory(name, sku)")
        .eq("purchase_order_id", poId),
    ]);

    const supplier = supplierRes.data as { name: string; phone: string | null; email: string | null } | null;
    const poLines = (polRes.data ?? []) as { id: string; inventory_id: string | null; description: string | null; quantity: number; inventory: { name: string; sku: string } | null }[];
    const deliveryLines = (dlRes.data ?? []) as { purchase_order_line_id: string; quantity_received: number }[];

    const lineMap = new Map(deliveryLines.map((dl) => [dl.purchase_order_line_id, dl.quantity_received]));
    const polMap = new Map(poLines.map((p) => [p.id, p]));

    const lines: { description: string; sku?: string; quantity: number }[] = [];
    if (deliveryLines.length > 0) {
      deliveryLines.forEach((dl) => {
        const pol = polMap.get(dl.purchase_order_line_id);
        if (pol) {
          const desc = pol.inventory ? pol.inventory.name : pol.description || "—";
          const sku = pol.inventory?.sku;
          lines.push({ description: desc, sku, quantity: dl.quantity_received });
        }
      });
    }
    if (lines.length === 0) {
      poLines.forEach((pol) => {
        const desc = pol.inventory ? pol.inventory.name : pol.description || "—";
        const sku = pol.inventory?.sku;
        lines.push({ description: desc, sku, quantity: pol.quantity });
      });
    }

    const business = bizRes.data as { name: string | null } | null;
    const profileData = profRes.data as { phone_number: string | null; business_email: string | null } | null;
    const branch = branchRes.data as { name: string } | null;

    return {
      businessName: business?.name ?? "—",
      businessPhone: profileData?.phone_number ?? null,
      businessEmail: profileData?.business_email ?? null,
      businessAddress: null,
      supplierName: supplier?.name ?? "—",
      supplierPhone: supplier?.phone ?? null,
      supplierEmail: supplier?.email ?? null,
      poNumber: po.po_number ?? null,
      deliveryDate: delivery.delivery_date,
      vehicleReg: delivery.vehicle_reg ?? null,
      docNumber: delivery.doc_number ?? null,
      branchName: branch?.name ?? null,
      lines,
      receivedBy: delivery.received_by ?? null,
      preparedByName: profile?.full_name || user?.email || "—",
      deliveryNotes: delivery.delivery_notes ?? null,
    };
  }, [effectiveBizId, profile?.full_name, user?.email]);

  const { data: printData, isLoading: printLoading } = useQuery({
    queryKey: ["delivery-note", printDeliveryId],
    queryFn: () => fetchDeliveryNoteData(printDeliveryId!),
    enabled: !!printDeliveryId,
  });

  useEffect(() => {
    if (printData && printDeliveryId) {
      const handleAfterPrint = () => {
        setPrintDeliveryId(null);
        window.removeEventListener("afterprint", handleAfterPrint);
      };
      window.addEventListener("afterprint", handleAfterPrint);
      window.print();
      return () => window.removeEventListener("afterprint", handleAfterPrint);
    }
  }, [printData, printDeliveryId]);

  if (role === "employee" && !employeeViewSettings.allowEmployeeTransportLogistics) {
    return (
      <RestrictedPageNotice
        title="Transport & Logistics is disabled"
        description="Your manager has turned off purchase orders and deliveries for employee accounts."
      />
    );
  }

  return (
    <div className="space-y-6">
      {successBanner && (
        <div className="rounded-lg border border-emerald-500/50 bg-emerald-500/10 px-4 py-3 text-sm font-medium text-emerald-700 dark:text-emerald-300">
          {successBanner}
        </div>
      )}

      <div>
        <h2 className="text-2xl font-display font-bold">Transport & Logistics</h2>
        <p className="text-sm text-muted-foreground">Purchase orders, deliveries, and stock receipt</p>
      </div>

      {(overduePos.length > 0 || dueSoonPos.length > 0) && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4" /> Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {overduePos.length > 0 && (
              <div>
                <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Overdue ({overduePos.length})</p>
                <ul className="mt-1 text-sm text-muted-foreground">
                  {overduePos.slice(0, 5).map((po) => (
                    <li key={po.id}>
                      {po.po_number || `PO ${po.id.slice(0, 8)}`} — {supplierName(po.supplier_id)} — expected {po.expected_delivery_date}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {dueSoonPos.length > 0 && (
              <div>
                <p className="text-sm font-medium">Due soon ({dueSoonPos.length})</p>
                <ul className="mt-1 text-sm text-muted-foreground">
                  {dueSoonPos.slice(0, 5).map((po) => (
                    <li key={po.id}>
                      {po.po_number || `PO ${po.id.slice(0, 8)}`} — {supplierName(po.supplier_id)} — expected {po.expected_delivery_date}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="orders">Purchase Orders</TabsTrigger>
          <TabsTrigger value="deliveries">Deliveries</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All status</SelectItem>
                {PO_STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSupplier} onValueChange={setFilterSupplier}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Supplier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All suppliers</SelectItem>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Dialog open={addPoOpen} onOpenChange={(open) => { setAddPoOpen(open); if (!open) resetPoForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Plus className="h-4 w-4 mr-2" /> Add Purchase Order
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>New Purchase Order</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreatePo} className="space-y-4">
                  <div>
                    <Label>Supplier</Label>
                    <Select value={poSupplierId} onValueChange={setPoSupplierId} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select supplier" />
                      </SelectTrigger>
                      <SelectContent>
                        {suppliers.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Order date</Label>
                      <Input type="date" value={poOrderDate} onChange={(e) => setPoOrderDate(e.target.value)} required />
                    </div>
                    <div>
                      <Label>Expected delivery</Label>
                      <Input type="date" value={poExpectedDate} onChange={(e) => setPoExpectedDate(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Input value={poNotes} onChange={(e) => setPoNotes(e.target.value)} placeholder="Optional" />
                  </div>
                  <div>
                    <Label>Line items</Label>
                    <div className="space-y-2 mt-2">
                      {poLinesForm.map((line, idx) => (
                        <div key={idx} className="flex gap-2 items-center border rounded p-2">
                          {line.inventory_id ? (
                            <Select
                              value={line.inventory_id ?? ""}
                              onValueChange={(v) =>
                                setPoLinesForm((prev) => {
                                  const next = [...prev];
                                  const inv = inventory.find((i) => i.id === v);
                                  next[idx] = { ...next[idx], inventory_id: v || null, description: inv?.name ?? "" };
                                  return next;
                                })
                              }
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue placeholder="Product" />
                              </SelectTrigger>
                              <SelectContent>
                                {inventory.map((i) => (
                                  <SelectItem key={i.id} value={i.id}>
                                    {i.name} ({i.sku})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              className="flex-1"
                              placeholder="Description"
                              value={line.description}
                              onChange={(e) =>
                                setPoLinesForm((prev) => {
                                  const next = [...prev];
                                  next[idx] = { ...next[idx], description: e.target.value };
                                  return next;
                                })
                              }
                            />
                          )}
                          <Input
                            type="number"
                            min={1}
                            className="w-20"
                            value={line.quantity}
                            onChange={(e) =>
                              setPoLinesForm((prev) => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], quantity: parseInt(e.target.value, 10) || 1 };
                                return next;
                              })
                            }
                          />
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            className="w-24"
                            placeholder="Cost"
                            value={line.unit_cost || ""}
                            onChange={(e) =>
                              setPoLinesForm((prev) => {
                                const next = [...prev];
                                next[idx] = { ...next[idx], unit_cost: parseFloat(e.target.value) || 0 };
                                return next;
                              })
                            }
                          />
                          <Button type="button" variant="ghost" size="icon" onClick={() => removePoLine(idx)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Select value={addLineType} onValueChange={(v) => setAddLineType(v as "inventory" | "freetext")}>
                          <SelectTrigger className="w-[120px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inventory">From catalog</SelectItem>
                            <SelectItem value="freetext">Free text</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button type="button" variant="outline" size="sm" onClick={addPoLine}>
                          <Plus className="h-4 w-4 mr-1" /> Add line
                        </Button>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setAddPoOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-primary">Create PO</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="h-4 w-4 text-primary" /> Purchase Orders
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredPos.length === 0 ? (
                purchaseOrders.length === 0 ? (
                  <p className="text-muted-foreground text-sm py-4 text-center">No purchase orders yet.</p>
                ) : (
                  <NoMatchingRecords />
                )
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2 font-semibold">PO</th>
                        <th className="text-left py-3 px-2 font-semibold">Supplier</th>
                        <th className="text-left py-3 px-2 font-semibold">Order date</th>
                        <th className="text-left py-3 px-2 font-semibold">Expected</th>
                        <th className="text-left py-3 px-2 font-semibold">Status</th>
                        <th className="text-right py-3 px-2 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPos.map((po) => (
                        <tr key={po.id} className="border-b border-border/50">
                          <td className="py-3 px-2 font-medium">{po.po_number || `PO-${po.id.slice(0, 8)}`}</td>
                          <td className="py-3 px-2">{po.supplier?.name ?? supplierName(po.supplier_id)}</td>
                          <td className="py-3 px-2 text-muted-foreground">{po.order_date}</td>
                          <td className="py-3 px-2 text-muted-foreground">{po.expected_delivery_date || "—"}</td>
                          <td className="py-3 px-2">
                            <Select value={po.status} onValueChange={(v) => handleUpdatePoStatus(po.id, v as POStatus)}>
                              <SelectTrigger className="w-[120px] h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PO_STATUS_OPTIONS.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-3 px-2 text-right space-x-1">
                            <Button type="button" variant="ghost" size="sm" onClick={() => openAddDelivery(po.id)}>
                              Add delivery
                            </Button>
                            <Button type="button" variant="ghost" size="icon" onClick={() => handleDeletePo(po.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deliveries" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Truck className="h-4 w-4 text-primary" /> Deliveries
              </CardTitle>
            </CardHeader>
            <CardContent>
              {deliveries.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">No deliveries yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2 font-semibold">PO</th>
                        <th className="text-left py-3 px-2 font-semibold">Date</th>
                        <th className="text-left py-3 px-2 font-semibold">Branch</th>
                        <th className="text-left py-3 px-2 font-semibold">Status</th>
                        <th className="text-right py-3 px-2 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveries.map((d) => {
                        const po = purchaseOrders.find((p) => p.id === d.purchase_order_id);
                        return (
                          <tr key={d.id} className="border-b border-border/50">
                            <td className="py-3 px-2 font-medium">{po?.po_number || `PO-${d.purchase_order_id.slice(0, 8)}`} — {supplierName(po?.supplier_id ?? "")}</td>
                            <td className="py-3 px-2 text-muted-foreground">{d.delivery_date}</td>
                            <td className="py-3 px-2 text-muted-foreground">{d.branch?.name ?? branchName(d.branch_id)}</td>
                            <td className="py-3 px-2">
                              <Select value={d.status} onValueChange={(v) => handleUpdateDeliveryStatus(d.id, v as DeliveryStatus)}>
                                <SelectTrigger className="w-[120px] h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {DELIVERY_STATUS_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>
                                      {o.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-3 px-2 text-right space-x-2">
                              {d.status === "delivered" && (
                                <>
                                  <Button type="button" variant="outline" size="sm" onClick={() => openReceive(d)}>
                                    Receive stock
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="no-print"
                                    onClick={() => setPrintDeliveryId(d.id)}
                                    disabled={printLoading && printDeliveryId === d.id}
                                  >
                                    {printLoading && printDeliveryId === d.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Printer className="h-4 w-4 mr-1" />
                                    )}
                                    Print Delivery Note
                                  </Button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={addDeliveryOpen} onOpenChange={(open) => { setAddDeliveryOpen(open); if (!open) setAddDeliveryPoId(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-card text-card-foreground border-border">
          <DialogHeader>
            <DialogTitle>Add Delivery</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateDelivery} className="space-y-4">
            <div>
              <Label>Delivery date</Label>
              <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} required />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={deliveryStatus} onValueChange={(v) => setDeliveryStatus(v as DeliveryStatus)}>
                <SelectTrigger className="bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Destination branch</Label>
              <Select value={deliveryBranchId || "__none__"} onValueChange={(v) => setDeliveryBranchId(v === "__none__" ? "" : v)}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Select (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {branches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Carrier / Tracking</Label>
              <Input value={deliveryTracking} onChange={(e) => setDeliveryTracking(e.target.value)} placeholder="Optional" />
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={deliveryNotes} onChange={(e) => setDeliveryNotes(e.target.value)} placeholder="Optional" />
            </div>
            <Button type="submit" className="w-full">Add delivery</Button>
          </form>
        </DialogContent>
      </Dialog>

      {printData && createPortal(<DeliveryNote data={printData} />, document.body)}

      <Dialog open={!!receiveDelivery} onOpenChange={(open) => { if (!open) setReceiveDelivery(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Receive stock</DialogTitle>
          </DialogHeader>
          {receiveDelivery && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter quantities received for each line. Stock will be added to inventory.
              </p>
              <div className="space-y-2">
                {poLines
                  .filter((l) => l.purchase_order_id === receiveDelivery.purchase_order_id)
                  .map((l) => (
                    <div key={l.id} className="flex items-center gap-2">
                      <span className="flex-1 text-sm">{getLineDisplay(l)} (ordered: {l.quantity})</span>
                      <Input
                        type="number"
                        min={0}
                        max={l.quantity}
                        className="w-24"
                        value={receiveQuantities[l.id] ?? 0}
                        onChange={(e) =>
                          setReceiveQuantities((prev) => ({ ...prev, [l.id]: parseInt(e.target.value, 10) || 0 }))
                        }
                      />
                    </div>
                  ))}
              </div>
              <div>
                <Label>Received By</Label>
                <Input
                  value={receiveReceivedBy}
                  onChange={(e) => setReceiveReceivedBy(e.target.value)}
                  placeholder="Name of person who signed for delivery"
                />
              </div>
              <div>
                <Label>Vehicle Reg</Label>
                <Input
                  value={receiveVehicleReg}
                  onChange={(e) => setReceiveVehicleReg(e.target.value)}
                  placeholder="Supplier vehicle registration (optional)"
                />
              </div>
              <div>
                <Label>Delivery Notes</Label>
                <Textarea
                  value={receiveDeliveryNotes}
                  onChange={(e) => setReceiveDeliveryNotes(e.target.value)}
                  placeholder="Optional notes"
                  rows={3}
                />
              </div>
              <Button className="w-full" onClick={handleReceive} disabled={receiving}>
                {receiving ? "Receiving..." : "Confirm receipt"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
